import { useState } from 'react';
import type { ReactNode } from 'react';
import { useAction, useApi, useConsole, useRead } from '../../context';
import { Confirm, Failure, Loading, Rich } from '../../bits';
import { Pager } from '../../Fleet';
import { Chip, Panel, Segmented } from '../../kit';
import type { ChipTone } from '../../kit';
import { useLocale, useT } from '../../../messages';
import type { T } from '../../../messages';
import { DeliveryStatus } from '../../../wire';
import type { Delivery, WebhookEndpoint } from '../../../wire';
import { Dialog } from './Dialog';
import {
  OFFERED_EVENTS,
  deliveryWord,
  eventName,
  looksLikeSecret,
  moment,
  relative,
} from './words';

/**
 * **Booking updates to partners** — where a business's booking changes are
 * sent, and whether they got there. The endpoints card and the delivery log,
 * merged, leading with the one thing an operator opens it to learn: "All
 * updates delivered", or "3 gave up since Tuesday · Resend them".
 *
 * Two things this card must not become, both carried over from the pair it
 * replaces:
 *
 *  * **A place a secret is typed.** The address's signing secret is kept in
 *    Secret Manager and only its *name* is written here; the server refuses a
 *    value that looks like the secret itself, and the field says so before the
 *    click rather than after it.
 *  * **A payload viewer.** A delivery carries no payload on this surface — it is
 *    the one field that can hold a client's name — so History shows what an
 *    operator needs to fix a delivery (what happened, how many tries, what the
 *    far end answered) and says why the rest is not there.
 *
 * Resending is routine rather than heroic: the event record is append-only and
 * complete, and partners ignore an update they already have, so resending more
 * than was lost is safe by construction. It always has a lower bound — the
 * oldest update that gave up, or a moment the operator picks in History —
 * because a resend with no bound is every update the business ever made.
 *
 * The health lead reads two short lists (gave up, retrying) rather than a
 * count: counting an append-only table is the unbounded read the log's pager
 * already refuses, so a full page is said as "50 or more".
 */
export function UpdatesCard({
  tenantId,
  endpoints,
  conciergeOwns,
  onChanged,
}: {
  tenantId: string;
  endpoints: readonly WebhookEndpoint[];
  /** The concierge card is showing the concierge's own address — leave it out here. */
  conciergeOwns: boolean;
  onChanged: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const locale = useLocale();
  const { config } = useConsole();
  const resend = useAction();
  const write = useAction();

  // Bumped after every resend so History, if open, reads again too.
  const [version, setVersion] = useState(0);
  const gaveUp = useRead(
    () => api.deliveries(tenantId, { status: 'exhausted', limit: config.pageSize }),
    [tenantId, config.pageSize, version],
  );
  const retrying = useRead(
    () => api.deliveries(tenantId, { status: 'failed', limit: config.pageSize }),
    [tenantId, config.pageSize, version],
  );

  const [since, setSince] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [editing, setEditing] = useState<WebhookEndpoint | 'new' | null>(null);
  const [stopping, setStopping] = useState<WebhookEndpoint | null>(null);

  const shown = endpoints.filter((e) => !(conciergeOwns && e.integration === 'aiconcierge'));
  const gave = gaveUp.value?.deliveries ?? [];
  const more = gave.length >= (gaveUp.value?.limit ?? Infinity);
  const oldest = gave.reduce<string | null>(
    (min, d) => (min === null || d.occurred_at < min ? d.occurred_at : min),
    null,
  );
  const retryCount = retrying.value?.deliveries.length ?? 0;
  const anyFailing = endpoints.some((e) => e.failing);
  const allStopped = endpoints.length > 0 && endpoints.every((e) => !e.active);

  const health = ((): { tone: ChipTone | undefined; chip: string; lead: ReactNode } => {
    if (endpoints.length === 0) {
      return {
        tone: 'plain',
        chip: t('connections.updates.chip.none'),
        lead: t('connections.updates.none'),
      };
    }
    if (gave.length > 0 && oldest !== null) {
      return {
        tone: 'danger',
        chip: t('connections.updates.chip.gaveUp', { count: gave.length }),
        lead: (
          <>
            {t('connections.updates.gaveUp', {
              count: gave.length,
              since: moment(oldest, locale),
              more,
            })}{' '}
            <button
              type="button"
              className="button button--sm"
              onClick={() => {
                resend.clear();
                setSince(oldest);
              }}
            >
              {t('connections.updates.resend')}
            </button>
          </>
        ),
      };
    }
    if (anyFailing) {
      return {
        tone: 'danger',
        chip: t('connections.updates.chip.failing'),
        lead: t('connections.updates.failing'),
      };
    }
    if (retryCount > 0) {
      return {
        tone: 'warn',
        chip: t('connections.updates.chip.retrying'),
        lead: t('connections.updates.retrying', { count: retryCount }),
      };
    }
    if (allStopped) {
      return {
        tone: 'warn',
        chip: t('connections.updates.state.stopped'),
        lead: t('connections.updates.stopped'),
      };
    }
    return {
      tone: 'ok',
      chip: t('connections.updates.chip.ok'),
      lead: t('connections.updates.ok'),
    };
  })();

  const confirmResend = async (): Promise<void> => {
    if (since === null) return;
    const ok = await resend.run(async () => {
      const answer = await api.requeue(tenantId, since);
      setNote(
        t('connections.updates.resent', {
          count: answer.requeued + answer.created,
          since: moment(answer.since, locale),
        }),
      );
    });
    if (ok) {
      setSince(null);
      setVersion((v) => v + 1);
      onChanged();
    }
  };

  const setActive = async (endpoint: WebhookEndpoint, active: boolean): Promise<boolean> => {
    const ok = await write.run(async () => {
      await api.setWebhook(tenantId, {
        integration: endpoint.integration,
        url: endpoint.url,
        secret_ref: endpoint.secret_ref,
        signature_header: endpoint.signature_header,
        event_types: endpoint.event_types,
        active,
      });
      setNote(
        active
          ? t('connections.updates.started', { integration: endpoint.integration })
          : t('connections.updates.stoppedDone', { integration: endpoint.integration }),
      );
    });
    if (ok) onChanged();
    return ok;
  };

  const loading = gaveUp.value === null && gaveUp.loading;

  return (
    <Panel
      title={t('connections.updates.title')}
      className="conn-card conn-card--wide"
      actions={loading ? undefined : <Chip tone={health.tone}>{health.chip}</Chip>}
    >
      {gaveUp.failure !== null ? <Failure failure={gaveUp.failure} /> : null}
      {loading ? null : <p className="conn-lead">{health.lead}</p>}
      {note !== null ? <p className="ok">{note}</p> : null}
      {since === null && resend.failure !== null ? <Failure failure={resend.failure} /> : null}
      {stopping === null && write.failure !== null ? <Failure failure={write.failure} /> : null}

      {shown.length > 0 ? (
        <>
          <h4 className="conn-sub">{t('connections.updates.addresses')}</h4>
          <ul className="conn-list">
            {shown.map((e) => (
              <li key={e.endpoint_id} className="conn-row">
                <div className="conn-row__main">
                  <div className="conn-row__line">
                    <strong>{e.integration}</strong> <span className="mono wrap">{e.url}</span>{' '}
                    {e.active ? null : (
                      <Chip tone="warn">{t('connections.updates.state.stopped')}</Chip>
                    )}
                    {e.failing ? (
                      <Chip tone="danger">{t('connections.updates.state.failing')}</Chip>
                    ) : null}
                  </div>
                  <div className="muted conn-small">
                    {t('connections.updates.sends', { what: eventsWords(t, e.event_types) })}
                    {' · '}
                    <Rich
                      text={t('connections.updates.signedWith', { header: e.signature_header })}
                    />
                  </div>
                </div>
                <div className="row-actions">
                  <button
                    type="button"
                    className="button button--sm"
                    onClick={() => {
                      write.clear();
                      setEditing(e);
                    }}
                  >
                    {t('connections.updates.edit')}
                  </button>
                  {e.active ? (
                    <button
                      type="button"
                      className="button button--sm"
                      onClick={() => {
                        write.clear();
                        setStopping(e);
                      }}
                    >
                      {t('connections.updates.stop')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="button button--sm"
                      disabled={write.busy}
                      onClick={() => void setActive(e, true)}
                    >
                      {t('connections.updates.start')}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {conciergeOwns && shown.length < endpoints.length ? (
        <p className="muted conn-small">{t('connections.updates.conciergeElsewhere')}</p>
      ) : null}

      <div className="row-actions">
        <button type="button" className="button button--sm" onClick={() => setEditing('new')}>
          {t('connections.updates.add')}
        </button>
      </div>

      <History tenantId={tenantId} version={version} onResend={setSince} />

      {editing !== null ? (
        <EndpointDialog
          tenantId={tenantId}
          endpoints={endpoints}
          editing={editing === 'new' ? null : editing}
          onSaved={(sentence) => {
            setNote(sentence);
            setEditing(null);
            onChanged();
          }}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {stopping !== null ? (
        <Confirm
          title={t('connections.updates.stopTitle', { integration: stopping.integration })}
          consequence={t('connections.updates.stopConsequence', { url: stopping.url })}
          confirmLabel={t('connections.updates.stopLabel')}
          busy={write.busy}
          failure={write.failure}
          onConfirm={() =>
            void (async () => {
              if (await setActive(stopping, false)) setStopping(null);
            })()
          }
          onCancel={() => {
            write.clear();
            setStopping(null);
          }}
        />
      ) : null}

      {since !== null ? (
        <Confirm
          title={t('connections.updates.resendTitle', { since: moment(since, locale) })}
          consequence={t('connections.updates.resendConsequence')}
          confirmLabel={t('connections.updates.resendLabel')}
          tone="primary"
          busy={resend.busy}
          failure={resend.failure}
          onConfirm={() => void confirmResend()}
          onCancel={() => {
            resend.clear();
            setSince(null);
          }}
        />
      ) : null}
    </Panel>
  );
}

function eventsWords(t: T, types: readonly string[]): string {
  if (types.length === 0) return t('connections.updates.everything');
  return types.map((type) => eventName(t, type)).join(', ');
}

/* ------------------------------------------------------------------ */
/* History                                                             */
/* ------------------------------------------------------------------ */

type Filter = 'all' | DeliveryStatus;

/**
 * The delivery log, one click deeper. It is read only once opened: a card
 * whose job is the verdict should not page through the log to give it.
 */
function History({
  tenantId,
  version,
  onResend,
}: {
  tenantId: string;
  version: number;
  onResend: (sinceIso: string) => void;
}): ReactNode {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <details className="conn-history" onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>{t('connections.history.title')}</summary>
      {open ? <HistoryBody tenantId={tenantId} version={version} onResend={onResend} /> : null}
    </details>
  );
}

function HistoryBody({
  tenantId,
  version,
  onResend,
}: {
  tenantId: string;
  version: number;
  onResend: (sinceIso: string) => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const { config } = useConsole();
  const [filter, setFilter] = useState<Filter>('all');
  const [offset, setOffset] = useState(0);
  const [at, setAt] = useState('');

  const page = useRead(
    () =>
      api.deliveries(tenantId, {
        limit: config.pageSize,
        offset,
        status: filter === 'all' ? undefined : filter,
      }),
    [tenantId, filter, offset, config.pageSize, version],
  );

  const options = [
    { id: 'all' as const, label: t('connections.history.filter.all') },
    ...DeliveryStatus.options.map((s) => ({ id: s, label: deliveryWord(t, s) })),
  ];

  return (
    <div className="conn-history__body">
      <div className="conn-history__bar">
        <Segmented
          label={t('connections.history.filter')}
          options={options}
          value={filter}
          onChange={(next) => {
            setOffset(0);
            setFilter(next);
          }}
        />
        <label className="inline-field">
          <span>{t('connections.history.resendSince')}</span>
          <input
            type="datetime-local"
            value={at}
            onChange={(e) => setAt(e.target.value)}
            aria-label={t('connections.history.resendSince')}
          />
        </label>
        <button
          type="button"
          className="button button--sm"
          disabled={at === ''}
          onClick={() => {
            // `datetime-local` is a wall clock with no zone. Sending it raw would
            // make the bound mean something different in St Thomas than on the
            // laptop; the server wants ISO-8601 and gets it, converted once, here.
            const parsed = new Date(at);
            onResend(Number.isNaN(parsed.getTime()) ? at : parsed.toISOString());
          }}
        >
          {t('connections.history.resendSinceGo')}
        </button>
      </div>

      {page.failure !== null ? <Failure failure={page.failure} /> : null}
      {page.value === null ? (
        page.loading ? (
          <Loading what={t('loading.deliveries')} />
        ) : null
      ) : page.value.deliveries.length === 0 ? (
        <p className="empty">
          {filter === 'all'
            ? t('connections.history.empty')
            : t('connections.history.emptyFiltered')}
        </p>
      ) : (
        <>
          <ul className="conn-list">
            {page.value.deliveries.map((d) => (
              <DeliveryRow key={d.delivery_id} delivery={d} />
            ))}
          </ul>
          <Pager
            count={page.value.deliveries.length}
            limit={page.value.limit}
            offset={page.value.offset}
            busy={page.loading}
            onOffset={setOffset}
          />
        </>
      )}
    </div>
  );
}

function deliveryTone(status: string): ChipTone | undefined {
  switch (status) {
    case 'delivered':
      return 'ok';
    case 'failed':
      return 'warn';
    case 'exhausted':
      return 'danger';
    default:
      return undefined;
  }
}

function DeliveryRow({ delivery: d }: { delivery: Delivery }): ReactNode {
  const t = useT();
  const locale = useLocale();
  const [open, setOpen] = useState(false);

  return (
    <li className="conn-row conn-row--stack">
      <div className="conn-row__line">
        <strong>{eventName(t, d.event_type)}</strong>{' '}
        <span className="muted">
          {t('connections.history.to', { integration: d.integration })}
          {' · '}
          <span title={d.occurred_at}>{relative(d.occurred_at, locale)}</span>
          {' · '}
          {t('connections.history.tries', { count: d.attempts })}
        </span>{' '}
        <Chip tone={deliveryTone(d.status)}>{deliveryWord(t, d.status)}</Chip>{' '}
        <button
          type="button"
          className="linkish"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? t('connections.history.hide') : t('connections.history.details')}
        </button>
      </div>
      {open ? (
        <div className="conn-detail">
          <p className="wrap">{t('connections.history.sentTo', { url: d.url })}</p>
          <p>
            {d.delivered_at !== null ? (
              <span title={d.delivered_at}>
                {t('connections.history.deliveredAt', { when: relative(d.delivered_at, locale) })}
              </span>
            ) : d.status === 'exhausted' ? (
              t('connections.history.noMoreTries')
            ) : (
              <span title={d.next_attempt_at}>
                {t('connections.history.nextTry', { when: relative(d.next_attempt_at, locale) })}
              </span>
            )}{' '}
            {d.last_status_code !== null
              ? t('connections.history.answered', { code: String(d.last_status_code) })
              : d.delivered_at === null && d.attempts > 0
                ? t('connections.history.noAnswer')
                : null}
          </p>
          {/* The far end's own error text, printed as it arrived. */}
          {d.last_error === null ? null : (
            <>
              <p className="muted">{t('connections.history.said')}</p>
              <pre className="error-text">{d.last_error}</pre>
            </>
          )}
          <details className="conn-more">
            <summary>{t('connections.history.ids')}</summary>
            <p className="mono conn-small">
              {d.delivery_id}
              <br />
              {d.event_id}
            </p>
          </details>
          <p className="muted conn-small">{t('connections.history.noPayload')}</p>
        </div>
      ) : null}
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* Set or change an address                                            */
/* ------------------------------------------------------------------ */

function EndpointDialog({
  tenantId,
  endpoints,
  editing,
  onSaved,
  onClose,
}: {
  tenantId: string;
  endpoints: readonly WebhookEndpoint[];
  editing: WebhookEndpoint | null;
  onSaved: (sentence: string) => void;
  onClose: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();

  const [integration, setIntegration] = useState(editing?.integration ?? 'aiconcierge');
  const [url, setUrl] = useState(editing?.url ?? '');
  const [secretRef, setSecretRef] = useState(editing?.secret_ref ?? '');
  const [header, setHeader] = useState(editing?.signature_header ?? 'X-Provider-Signature');
  const [every, setEvery] = useState((editing?.event_types.length ?? 0) === 0);
  const [types, setTypes] = useState<string[]>(editing?.event_types ?? []);
  const [confirming, setConfirming] = useState(false);

  const who = integration.trim();
  const existing = endpoints.find((e) => e.integration === who) ?? null;
  // Any type the endpoint already filters on stays offered, by its token if
  // this console has no words for it, so editing never silently drops one.
  const offered = [...OFFERED_EVENTS, ...types.filter((x) => !OFFERED_EVENTS.includes(x as never))];
  const secretLooksReal = looksLikeSecret(secretRef);
  const ready =
    who !== '' &&
    url.trim() !== '' &&
    secretRef.trim() !== '' &&
    !secretLooksReal &&
    (every || types.length > 0);

  const save = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.setWebhook(tenantId, {
        integration: who,
        url: url.trim(),
        secret_ref: secretRef.trim(),
        signature_header: header.trim(),
        event_types: every ? [] : types,
      });
      onSaved(
        t('connections.endpoint.saved', { integration: answer.integration, url: answer.url }),
      );
    });
    if (!ok) setConfirming(false);
  };

  const title =
    editing === null
      ? t('connections.endpoint.titleNew')
      : t('connections.endpoint.titleEdit', { integration: editing.integration });

  return (
    <>
      <Dialog title={title} onClose={onClose} busy={action.busy} wide>
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!ready) return;
            // Replacing an address that is already set is the consequential
            // case — updates start going somewhere else — so it asks first.
            if (existing !== null) setConfirming(true);
            else void save();
          }}
        >
          <label className="field">
            <span className="field__label">{t('connections.endpoint.who')}</span>
            <input
              aria-label={t('connections.endpoint.who')}
              value={integration}
              onChange={(e) => setIntegration(e.target.value)}
              disabled={editing !== null}
            />
            <span className="field__hint">{t('connections.endpoint.whoHint')}</span>
          </label>
          <label className="field">
            <span className="field__label">{t('connections.endpoint.url')}</span>
            <input
              aria-label={t('connections.endpoint.url')}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://partner.example/hooks/yourstore"
            />
            <span className="field__hint">{t('connections.endpoint.urlHint')}</span>
          </label>
          <label className="field">
            <span className="field__label">{t('connections.endpoint.secret')}</span>
            <input
              aria-label={t('connections.endpoint.secret')}
              value={secretRef}
              onChange={(e) => setSecretRef(e.target.value)}
              placeholder="projects/yourstore-dev/secrets/partner-webhook-secret/versions/latest"
              aria-invalid={secretLooksReal}
            />
            <span className="field__hint">{t('connections.endpoint.secretHint')}</span>
            {secretLooksReal ? (
              <span className="warn" role="alert">
                {t('connections.endpoint.secretLooksReal')}
              </span>
            ) : null}
          </label>
          <label className="field">
            <span className="field__label">{t('connections.endpoint.header')}</span>
            <input
              aria-label={t('connections.endpoint.header')}
              value={header}
              onChange={(e) => setHeader(e.target.value)}
            />
            <span className="field__hint">{t('connections.endpoint.headerHint')}</span>
          </label>

          <fieldset className="conn-choices">
            <legend>{t('connections.endpoint.events')}</legend>
            <label className="conn-choice">
              <input
                type="radio"
                name="endpoint-events"
                checked={every}
                onChange={() => setEvery(true)}
              />
              <span>{t('connections.endpoint.events.all')}</span>
            </label>
            <label className="conn-choice">
              <input
                type="radio"
                name="endpoint-events"
                checked={!every}
                onChange={() => setEvery(false)}
              />
              <span>{t('connections.endpoint.events.some')}</span>
            </label>
            {every ? null : (
              <div className="conn-events">
                {offered.map((type) => (
                  <label key={type} className="scope">
                    <input
                      type="checkbox"
                      checked={types.includes(type)}
                      onChange={(e) =>
                        setTypes((list) =>
                          e.target.checked ? [...list, type] : list.filter((x) => x !== type),
                        )
                      }
                    />
                    <span>{eventName(t, type)}</span>
                  </label>
                ))}
                {types.length === 0 ? (
                  <p className="muted">{t('connections.endpoint.events.pickOne')}</p>
                ) : null}
              </div>
            )}
          </fieldset>

          {!confirming && action.failure !== null ? <Failure failure={action.failure} /> : null}

          <div className="modal__actions">
            <button type="button" className="button" onClick={onClose} disabled={action.busy}>
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="button button--primary"
              disabled={action.busy || !ready}
            >
              {action.busy ? t('common.saving') : t('connections.endpoint.save')}
            </button>
          </div>
        </form>
      </Dialog>
      {confirming && existing !== null ? (
        <Confirm
          title={t('connections.endpoint.overwriteTitle', { integration: existing.integration })}
          consequence={t('connections.endpoint.overwriteConsequence', {
            before: existing.url,
            after: url.trim(),
          })}
          confirmLabel={t('connections.endpoint.overwriteLabel')}
          tone="primary"
          busy={action.busy}
          failure={action.failure}
          onConfirm={() => void save()}
          onCancel={() => {
            action.clear();
            setConfirming(false);
          }}
        />
      ) : null}
    </>
  );
}
